/**
 * The parental-controls rule catalogue (fix Z2.1) — V2-native, so both the
 * Info tab's section and the action bar's popover can draw a title + a
 * plain-English detail line without reaching into the OLD
 * `GameRulesComponent` for its copy (`rail/info/InfoPanel/View.tsx`'s own
 * `require`, now deleted — its popover cannot live inside the rail's
 * `overflow: hidden` card to begin with).
 *
 * Mirrors that component's own `rulesDescriptions` map
 * (`src/Screens/PlayChess/components/GameRulesComponent/index.tsx` L31-42),
 * rewritten PER RULE rather than copied verbatim: its one shared sentence —
 * "if you break these rules you will lose the game instantly" — is false for
 * two of the five (`thinking_time_rule` forces a pause, it does not end the
 * game; `games_reviewed_rule` blocks a NEW game, not the one in progress).
 */
export type ParentalRule = {key: string; title: string; detail: string};

export const PARENTAL_RULES: Record<string, {title: string; detail: string}> = {
  castling_rule: {
    title: 'Castle early',
    detail:
      'Castle within the first 15 moves, or the game is lost by auto-resignation.',
  },
  thinking_time_rule: {
    title: 'Thinking time',
    detail:
      'A forced pause before each move from the 5th move on, to avoid quick blunders.',
  },
  rooks_connected_rule: {
    title: 'Connected rooks',
    detail:
      'Keep the rooks connected after the 20th move, or the game is lost by auto-resignation.',
  },
  games_reviewed_rule: {
    title: 'Review before you play',
    detail: "Yesterday's games must be reviewed before a new game can start.",
  },
  touch_piece_rule: {
    title: 'Touch-move',
    detail: 'A touched piece must be moved, on click or drag.',
  },
};

/**
 * "foo_bar_rule" -> "Foo bar rule" — underscores to spaces, sentence case.
 * The old component showed the raw key for one it had no copy for; this is
 * the same "never drop it" choice, just readable.
 */
const humanise = (key: string): string => {
  const words = key.split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Keeps the CALLER's own order (the account's rules, server-side) rather
 * than the catalogue's; an unknown key is humanised rather than dropped —
 * `GameRulesComponent`'s own `rules.map(rule => rulesDescriptions[rule] ||
 * rule)` kept the raw key sooner than silently hiding a rule nobody had
 * written copy for yet.
 */
export const parentalRulesFor = (keys: string[]): ParentalRule[] =>
  keys.map(key => {
    const known = PARENTAL_RULES[key];
    return known
      ? {key, title: known.title, detail: known.detail}
      : {key, title: humanise(key), detail: ''};
  });

export default parentalRulesFor;
